import {Sprite, Texture} from "pixi.js";
import {VisualMixin, visualize} from "../mixins";
import {MediaClip} from "../media";
import { applyShapeMask, type MaskShape } from "../../utils/shapeMask";

import type {Track} from "../../tracks";
import type {ImageClipProps} from "./image.interfaces";
import {Timestamp} from "../../models";
import {IOError} from "../../errors";
import {MediaSourceRef} from "../../sources/media-source";

const HALF_PI = Math.PI / 2;

export class ImageClip extends VisualMixin(MediaClip<ImageClipProps>) {
    public readonly type = "image";
    public track?: Track<ImageClip>;

    public source: MediaSourceRef;

    private readonly canvas = document.createElement("canvas");
    private readonly context = this.canvas.getContext("2d")!;

    public readonly textures = {
        canvas: Texture.from(this.canvas),
    };

    private readonly sprite = new Sprite(this.textures.canvas);

    private _lastW = 0;
    private _lastH = 0;

    // === CROP ===
    // Initialize to full image (no crop) so getCrop() always returns a valid value
    private _crop: { x: number; y: number; width: number; height: number } = { x: 0, y: 0, width: 1, height: 1 };
    // Original source dimensions (before any crop)
    private _originalSourceWidth?: number;
    private _originalSourceHeight?: number;
    // Cached loaded image for crop operations
    private _loadedImage?: HTMLImageElement;

    public constructor(source: MediaSourceRef, props: ImageClipProps = {duration: Timestamp.fromSeconds(5)}, id?: string) {
        super();
        if (id) {
            this.id = id;
        }

        this.source = source;

        (this as any).props = props;
        Object.assign(this, props);

        if (!props.duration) {
            this.duration.seconds = 5;
            this.range[1].seconds = this.duration.seconds;
        }

        this.sprite.anchor.set(0.5, 0.5);
        this.sprite.position.set(0, 0);
        this.sprite.height = props.height as number;
        this.sprite.width = props.width as number;

        this.view.addChild(this.sprite);

        this.init().then(() => {
        });
    }

    public async init(): Promise<void> {
        if (!this.source) {
            this.state = "ERROR";
            throw new IOError({
                code: "noSource",
                message: "ImageClip has no source set.",
            });
        }

        try {
            // however your ImageSourceRef exposes the URL
            const url: string | undefined = this.source.imgUrl;

            if (!url) {
                this.state = "ERROR";
                throw new IOError({
                    code: "noSourceUrl",
                    message: "ImageSourceRef has no uri/url.",
                });
            }

            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = (e) => reject(e ?? new Error("Failed to load image"));
                i.src = url;

                // cached case
                if (i.complete && i.naturalWidth > 0 && i.naturalHeight > 0) {
                    resolve(i);
                }
            });

            const canvas = document.createElement("canvas");

            // Respect clip props width/height if present, otherwise use image intrinsic size
            const targetWidth = (this as any).width ?? img.naturalWidth ?? img.width;
            const targetHeight = (this as any).height ?? img.naturalHeight ?? img.height;

            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const ctx = canvas.getContext("2d");
            if (!ctx) return null;

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            this.state = "READY";
        } catch (err) {
            console.error("[ImageClip.init] failed to load image", err);
            this.state = "ERROR";
            throw new IOError({
                code: "sourceNotProcessable",
                message: "An error occurred while loading the image.",
            });
        }
    }

    public async pullVideoFrame(compTimeSec: number): Promise<HTMLCanvasElement | null> {
        const clipLocalT = this.CompositionTimeToClipTime(compTimeSec);

        // Optional: only show image while playhead is within clip duration
        if (clipLocalT < 0 || clipLocalT > this.duration.seconds) {
            return null;
        }

        const url = (this.source as any)?.imgUrl as string | undefined;
        if (!url) return null;

        // Load the image
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = (e) => reject(e ?? new Error("Failed to load image"));
            i.src = url;

            // If already cached
            if (i.complete && i.naturalWidth > 0 && i.naturalHeight > 0) {
                resolve(i);
            }
        });

        // Create a canvas and draw the image into it
        const canvas = document.createElement("canvas");

        // Respect clip props width/height if present, otherwise use image intrinsic size
        const targetWidth = (this as any).width ?? img.naturalWidth ?? img.width;
        const targetHeight = (this as any).height ?? img.naturalHeight ?? img.height;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        return canvas;
    }

    /**
     * Called every render tick by Track.update() → Composition.computeFrameAtTime().
     * Responsible for uploading the latest frame into this clip's display texture.
     */
    @visualize
    public async update(time: Timestamp): Promise<void> {
        // Apply optional shape mask (circle, etc.). Uses per-scene overrides when present.
        try {
            const sceneKey = ((this.track?.composition as any)?.currentSceneKey as string | undefined) ?? "__start__";
            const w = Number(this.track?.composition?.width ?? 1920) || 1920;
            const h = Number(this.track?.composition?.height ?? 1080) || 1080;
            const ratio = h > 0 ? w / h : 16 / 9;
            const aspect = Math.abs(ratio - 1) < 0.06 ? "square" : ratio < 1 ? "vertical" : "horizontal";
            const ov =
                ((this as any)?.sceneVisualOverridesByAspect &&
                    typeof (this as any).sceneVisualOverridesByAspect === "object" &&
                    (this as any).sceneVisualOverridesByAspect[aspect] &&
                    typeof (this as any).sceneVisualOverridesByAspect[aspect] === "object" &&
                    (this as any).sceneVisualOverridesByAspect[aspect][sceneKey]) ||
                (this as any)?.sceneVisualOverrides?.[sceneKey];
            const shape: MaskShape =
                typeof ov?.maskShape === "string"
                    ? (ov.maskShape as MaskShape)
                    : ((this as any)?.maskShape as MaskShape) ?? "none";
            applyShapeMask(this.view as any, shape);
        } catch {}

        // Apply visual fade-in/out transitions by modulating alpha.
        try {
            const mul = this.getVisualFadeMultiplier(time.seconds);
            if (Number.isFinite(mul) && mul !== 1) {
                this.view.alpha = this.view.alpha * mul;
            }
        } catch {}

        // VIDEO PATH
        const maybeCanvas = await this.pullVideoFrame(time.seconds);
        if (maybeCanvas) {
            const src = maybeCanvas;
            if (src) {
	                // Scene/aspect overrides are computed in the @visualize decorator and passed via __librettoEffectiveCrop.
	                const crop = (((this as any).__librettoEffectiveCrop as any) ?? this._crop) as any;
	                const isFullCrop = crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1;

                if (isFullCrop) {
                    // No crop - draw full image
                    if (src.width !== this._lastW || src.height !== this._lastH) {
                        this._lastW = src.width;
                        this._lastH = src.height;
                        this.canvas.width = src.width;
                        this.canvas.height = src.height;
                    }
                    this.context.drawImage(src, 0, 0);
                } else {
                    // Apply crop
                    const sx = Math.round(crop.x * src.width);
                    const sy = Math.round(crop.y * src.height);
                    const sw = Math.round(crop.width * src.width);
                    const sh = Math.round(crop.height * src.height);

                    // Resize canvas to cropped dimensions
                    if (sw !== this._lastW || sh !== this._lastH) {
                        this._lastW = sw;
                        this._lastH = sh;
                        this.canvas.width = sw;
                        this.canvas.height = sh;
                    }

                    // Draw cropped region
                    this.context.drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
                }

                // notify renderer that the texture changed
                this.textures.canvas.source.update();
            }
        }
    }


    public onSeek(_newCompTimeSec: number): void {
        // nothing to reset for static image
    }

    // ============ Image-specific timing overrides ============
    // Images can have arbitrary duration since they're static.
    // Override start/stop setters to allow growing/shrinking freely.

    /**
     * Get the start time on the composition timeline.
     */
    public get start(): Timestamp {
        return this.range[0].add(this._offset);
    }

    /**
     * Override start setter to allow extending the clip to the left.
     * For images, we can extend left by adjusting offset and range together.
     */
    public set start(time: number | Timestamp) {
        if (typeof time === 'number') {
            time = Timestamp.fromFrames(time);
        }

        const currentStart = this.range[0].add(this._offset);
        const currentStop = this.range[1].add(this._offset);
        const newStartSec = time.seconds;
        const currentStopSec = currentStop.seconds;
        
        // Don't allow start to go past stop
        if (newStartSec >= currentStopSec) {
            return;
        }
        
        // Calculate the new duration needed
        const newDurationSec = currentStopSec - newStartSec;
        
        // Set offset to new start position
        this._offset = Timestamp.fromSeconds(newStartSec);
        
        // Reset range to [0, newDuration]
        this.range[0] = new Timestamp();
        this.range[1] = Timestamp.fromSeconds(newDurationSec);
        
        // Update duration to match
        this.duration.seconds = newDurationSec;
        
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        
        try {
            // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch { /* noop */ }
    }

    /**
     * Get the stop time on the composition timeline.
     */
    public get stop(): Timestamp {
        return this.range[1].add(this._offset);
    }

    /**
     * Override stop setter to allow extending the clip to the right.
     * For images, we grow the duration when extending beyond current bounds.
     */
    public set stop(time: number | Timestamp) {
        if (typeof time === 'number') {
            time = Timestamp.fromFrames(time);
        }

        const currentStart = this.range[0].add(this._offset);
        const newStopSec = time.seconds;
        const currentStartSec = currentStart.seconds;
        
        // Don't allow stop to go before start
        if (newStopSec <= currentStartSec) {
            return;
        }
        
        // Calculate the new duration
        const newDurationSec = newStopSec - currentStartSec;
        
        // For images, we can freely set the duration
        this.duration.seconds = newDurationSec;
        this.range[1] = Timestamp.fromSeconds(newDurationSec);
        
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        
        try {
            // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch { /* noop */ }
    }

    /**
     * Set crop for this image clip.
     * Crop values are normalized 0-1 (x, y, width, height as fractions of original).
     */
    public setCrop(crop: { x: number; y: number; width: number; height: number } | undefined): void {
        // Store original dimensions on first crop if not already stored
        if (crop && (!this._originalSourceWidth || !this._originalSourceHeight)) {
            const currentW = typeof this._width === 'number' ? this._width : this.canvas.width || 1920;
            const currentH = typeof this._height === 'number' ? this._height : this.canvas.height || 1080;
            this._originalSourceWidth = currentW;
            this._originalSourceHeight = currentH;
        }

        this._crop = crop ?? { x: 0, y: 0, width: 1, height: 1 };

        // Update visual dimensions based on crop
        if (crop && this._originalSourceWidth && this._originalSourceHeight) {
            const newW = Math.round(this._originalSourceWidth * crop.width);
            const newH = Math.round(this._originalSourceHeight * crop.height);
            this._width = newW;
            this._height = newH;
        } else if (!crop && this._originalSourceWidth && this._originalSourceHeight) {
            this._width = this._originalSourceWidth;
            this._height = this._originalSourceHeight;
            this._lastW = 0;
            this._lastH = 0;
        }

        this.trigger('frame', undefined);
    }

    /**
     * Get crop for this image clip.
     * Always returns a valid crop config (defaults to full image if not cropped).
     */
    public getCrop(): { x: number; y: number; width: number; height: number } {
        return this._crop;
    }

    /**
     * Get the original source dimensions (before any crop).
     */
    public getOriginalSourceDimensions(): { width: number; height: number } | undefined {
        if (this._originalSourceWidth && this._originalSourceHeight) {
            return { width: this._originalSourceWidth, height: this._originalSourceHeight };
        }
        // If no crop has been applied yet, return current dimensions
        const w = typeof this._width === 'number' ? this._width : this.canvas.width;
        const h = typeof this._height === 'number' ? this._height : this.canvas.height;
        if (w && h) {
            return { width: w, height: h };
        }
        return undefined;
    }

    /**
     * Create a deep clone of this image clip with all properties.
     * The clone gets a new ID but shares the same source.
     */
    public copy(): ImageClip {
        // Get current width/height - critical for canvas initialization
        const currentWidth = (this as any)._width ?? (this as any).width ?? (this.canvas.width || 1920);
        const currentHeight = (this as any)._height ?? (this as any).height ?? (this.canvas.height || 1080);
        
        const clone = new ImageClip(this.source, {
            // Timeline props
            start: this.start,
            duration: this.duration,
            // Visual dimensions - CRITICAL for canvas to work
            width: currentWidth,
            height: currentHeight,
            // Opacity
            opacity: (this as any).opacity ?? this.sprite.alpha,
            // Transform if present
            transform: (this as any).props?.transform,
        });

        // Copy timing properties
        clone._offset = Timestamp.fromSeconds(this._offset.seconds);
        clone.duration.seconds = this.duration.seconds;
        clone.range = [
            Timestamp.fromSeconds(this.range[0].seconds),
            Timestamp.fromSeconds(this.range[1].seconds)
        ];
        
        // Copy visual properties (from VisualMixin)
        clone._position = { ...this._position };
        clone._width = currentWidth;
        clone._height = currentHeight;
        clone.alpha = this.alpha;
        clone.rotation = this.rotation;
        if (this._scale) clone._scale = { ...this._scale };
        clone.translate = { ...this.translate };
        
        // Copy crop settings
        clone._crop = { ...this._crop };
        if (this._originalSourceWidth) clone._originalSourceWidth = this._originalSourceWidth;
        if (this._originalSourceHeight) clone._originalSourceHeight = this._originalSourceHeight;
        
        // Copy cuts (deep clone with new IDs)
        clone.cuts = this.cuts.map(c => ({
            id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`,
            startSec: c.startSec,
            endSec: c.endSec,
            groupId: c.groupId
        }));

        // Copy visual fade configurations
        if (this.visualFadeIn) clone.visualFadeIn = { ...this.visualFadeIn };
        if (this.visualFadeOut) clone.visualFadeOut = { ...this.visualFadeOut };
        
        // Copy filters
        (clone.sprite as any).filters = (this.sprite as any).filters;
        
        // Copy name if set
        if (this.name) clone.name = this.name;

        return clone;
    }
    
    /**
     * Alias for copy() - for consistency with VideoClip and AudioClip.
     */
    public clone(): ImageClip {
        return this.copy();
    }

    /**
     * Compute a multiplier (0..1) for visual fade-in/out at clip edges.
     * Uses source-time internally so durations remain correct under playbackRate.
     */
    private getVisualFadeMultiplier(compTimeSec: number): number {
        const fadeIn = (this as any).visualFadeIn as any;
        const fadeOut = (this as any).visualFadeOut as any;
        const hasIn = fadeIn && typeof fadeIn.durationMs === "number" && fadeIn.durationMs > 0 && fadeIn.type !== "none";
        const hasOut = fadeOut && typeof fadeOut.durationMs === "number" && fadeOut.durationMs > 0 && fadeOut.type !== "none";
        if (!hasIn && !hasOut) return 1;

        const clipStartSrcSec = this.range?.[0]?.seconds ?? 0;
        const clipEndSrcSec = this.range?.[1]?.seconds ?? clipStartSrcSec;
        if (!Number.isFinite(clipStartSrcSec) || !Number.isFinite(clipEndSrcSec) || clipEndSrcSec <= clipStartSrcSec) return 1;

        const rate = Number.isFinite(this.playbackRate) && this.playbackRate > 0 ? this.playbackRate : 1;
        const tSrc = this.CompositionTimeToClipTime(compTimeSec);
        if (!Number.isFinite(tSrc)) return 1;

        let mul = 1;

        if (hasIn) {
            const durSrc = (Number(fadeIn.durationMs ?? 0) / 1000) * rate;
            if (durSrc > 0) {
                const endSrc = clipStartSrcSec + durSrc;
                if (tSrc >= clipStartSrcSec && tSrc < endSrc) {
                    const t = Math.max(0, Math.min(1, (tSrc - clipStartSrcSec) / durSrc));
                    mul *= Math.sin(t * HALF_PI);
                }
            }
        }

        if (hasOut) {
            const durSrc = (Number(fadeOut.durationMs ?? 0) / 1000) * rate;
            if (durSrc > 0) {
                const startSrc = clipEndSrcSec - durSrc;
                if (tSrc >= startSrc && tSrc < clipEndSrcSec) {
                    const t = Math.max(0, Math.min(1, (tSrc - startSrc) / durSrc));
                    mul *= Math.cos(t * HALF_PI);
                }
            }
        }

        return mul;
    }
}
